import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service'
import {useEffect, useState} from 'react'

import {useUpdateImmediateResults} from '../../../../contexts/update'
import useEventCallback from '../../../../hooks/useEventCallback'
import {restApi} from '../../../../services/rest'
import type {NormalizedBuild} from '../../../../types'
import {noop} from '../../../../utils/empty'
import {serializeError} from '../../PipelinesPages/utils/error'
import type {QueueBuildDragItem} from '../QueueBuild/QueueBuild'

const DEFAULT_TIMEOUT = 2000
const TIME_TO_BLINK = 500
export const firstBuildOrder = -1

export const useQueueBuildDragDrop = (builds: NormalizedBuild[]) => {
  const [droppedBuildId, setDroppedBuildId] = useState<number | undefined>(undefined)
  const [hoveredDropBuildId, setHoveredDropBuildId] = useState<number | undefined>(undefined)
  const [dropBuilds, setDropBuilds] = useState<NormalizedBuild[] | undefined>(undefined)

  useEffect(() => {
    if (droppedBuildId !== undefined) {
      const timeout = window.setTimeout(() => {
        setDroppedBuildId(undefined)
      }, TIME_TO_BLINK)

      return () => {
        clearTimeout(timeout)
      }
    }
    return noop
  }, [droppedBuildId])

  const updateImmediate = useUpdateImmediateResults()

  const [reorderBuildInQueue] = restApi.endpoints.setQueuedBuildPosition.useMutation()

  const handleDrop = useEventCallback((item: QueueBuildDragItem) => {
    if (hoveredDropBuildId === undefined || hoveredDropBuildId === item.buildId) {
      return
    }

    const dropIndex = builds.findIndex(build => build.id === item.buildId)
    let index = builds.findIndex(build => build.id === hoveredDropBuildId)

    if (dropIndex !== -1 && index !== -1) {
      if (dropIndex > index) {
        index++
      }
      const temp = [...builds.slice(0, dropIndex), ...builds.slice(dropIndex + 1)]

      setDropBuilds([...temp.slice(0, index), builds[dropIndex], ...temp.slice(index)])
    }

    reorderBuildInQueue({
      build: {id: item.buildId},
      queuePosition: hoveredDropBuildId === firstBuildOrder ? '1' : `after:${hoveredDropBuildId}`,
    })
      .then(result => {
        if ('error' in result) {
          alertService.error(serializeError(result.error), DEFAULT_TIMEOUT)
        }
      })
      .catch(error => {
        alertService.error(serializeError(error), DEFAULT_TIMEOUT)
      })
      .finally(() => {
        updateImmediate?.().finally(() => {
          setDroppedBuildId(item.buildId)
          setDropBuilds(undefined)
        })
      })
  })

  return {
    droppedBuildId,
    hoveredDropBuildId,
    setHoveredDropBuildId,
    dropBuilds,
    handleDrop,
  }
}
